import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Facebook, Twitter, Linkedin, Instagram, Youtube, ArrowRight } from 'lucide-react';

const StudyAbroadFooter = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-[#020617] text-white border-t border-white/5" data-testid="sa-footer">
      {/* CTA Banner */}
      <div className="bg-gradient-to-r from-[#003087] to-[#001F5C]">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h3 className="text-xl md:text-2xl font-bold font-syne text-white">
              Ready to Start Your Study Abroad Journey?
            </h3>
            <p className="text-slate-300 text-sm mt-1">
              Get expert guidance from certified counsellors. Your first consultation is free.
            </p>
          </div>
          <Link
            to="/study-abroad/book-counselling/"
            className="shrink-0 inline-flex items-center gap-2 bg-[#C5A028] hover:bg-[#EAB308] text-black font-bold text-sm px-8 py-3.5 rounded-full transition-all hover:shadow-lg hover:shadow-[#C5A028]/20"
            data-testid="sa-footer-cta"
          >
            Book Free Counselling
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>

      {/* Main Footer - 4 Columns */}
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Column 1: About & Authority */}
          <div>
            <div className="flex items-center gap-2 mb-5">
              <img
                src="/zafco-logo.png"
                alt="ZAFCO Study Abroad"
                className="h-12 w-auto"
              />
              <div className="border-l border-white/20 pl-2">
                <span className="text-white font-syne font-bold text-sm block">Study Abroad</span>
              </div>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              ZAFCO Study Abroad is the education arm of ZAFCO Human Resource Management, a
              Government of India (MEA) approved agency. We help Indian students secure
              admissions in top universities across Germany, UK, Canada, Australia &amp; Ireland.
            </p>
            <div className="flex items-center gap-1.5 mb-5">
              <span className="inline-block bg-[#003087]/30 border border-[#003087]/50 text-[10px] font-bold text-[#C5A028] px-2 py-1 rounded tracking-wider uppercase">
                MEA Approved
              </span>
            </div>
            <div className="flex gap-2.5">
              {[
                { id: 'fb', Icon: Facebook, href: '#', label: 'Facebook' },
                { id: 'ig', Icon: Instagram, href: '#', label: 'Instagram' },
                { id: 'li', Icon: Linkedin, href: '#', label: 'LinkedIn' },
                { id: 'tw', Icon: Twitter, href: '#', label: 'Twitter' },
                { id: 'yt', Icon: Youtube, href: '#', label: 'YouTube' },
              ].map(({ id, Icon, href, label }) => (
                <a
                  key={id}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-8 h-8 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center text-slate-400 hover:bg-[#C5A028] hover:text-black hover:border-[#C5A028] transition-all"
                  data-testid={`sa-social-${id}`}
                >
                  <Icon className="w-3.5 h-3.5" />
                </a>
              ))}
            </div>
          </div>

          {/* Column 2: Top Destinations */}
          <div>
            <h4 className="text-xs font-bold mb-5 font-syne uppercase tracking-wider text-white">
              Top Destinations
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Study in Germany', path: '/study-abroad/germany/' },
                { label: 'Study in New Zealand', path: '/study-abroad/newzealand/' },
                { label: 'Study in UK', path: '/study-abroad/uk/' },
                { label: 'Study in Canada', path: '/study-abroad/canada/' },
                { label: 'Study in Australia', path: '/study-abroad/australia/' },
                { label: 'Study in Ireland', path: '/study-abroad/ireland/' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm flex items-center gap-1.5 group"
                  >
                    <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity text-[#C5A028]" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>

            <h4 className="text-xs font-bold mt-8 mb-4 font-syne uppercase tracking-wider text-white">
              Quick Links
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Free Profile Evaluation', path: '/study-abroad/free-profile-evaluation/' },
                { label: 'Scholarship Assessment', path: '/study-abroad/scholarship-assessment/' },
                { label: 'Success Stories', path: '/study-abroad/student-success-stories/' },
                { label: 'Study Abroad Privacy Policy', path: '/study-abroad/privacy-policy/' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 3: Essential Visas & Guides */}
          <div>
            <h4 className="text-xs font-bold mb-5 font-syne uppercase tracking-wider text-white">
              Visa & Guides
            </h4>
            <ul className="space-y-2.5">
              {[
                { label: 'Germany Student Visa Guide', path: '/study-abroad/germany/student-visa/' },
                { label: 'UK Student Visa Guide', path: '/study-abroad/uk/student-visa/' },
                { label: 'APS Certificate Guide', path: '/study-abroad/germany/aps-certificate/' },
                { label: 'Germany Blocked Account', path: '/study-abroad/germany/blocked-account/' },
                { label: 'SOP Writing Guide', path: '/study-abroad/blog/how-to-write-sop-for-germany/' },
                { label: 'UK Best Universities', path: '/study-abroad/uk/best-universities/' },
                { label: 'Education Loan Assistance', path: '/study-abroad/education-loan-assistance/' },
              ].map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Column 4: Contact & CTA */}
          <div>
            <h4 className="text-xs font-bold mb-5 font-syne uppercase tracking-wider text-white">
              Contact Us
            </h4>
            <div className="space-y-4 mb-6">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#C5A028] mt-0.5 shrink-0" />
                <p className="text-slate-400 text-sm leading-relaxed">
                  ZAFCO Study Abroad Division<br />
                  New Delhi, India
                </p>
              </div>
              <a
                href="mailto:studyabroad@zafco.in"
                className="flex items-center gap-3 text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
              >
                <Mail className="w-4 h-4 text-[#C5A028] shrink-0" />
                studyabroad@zafco.in
              </a>
              <a
                href="tel:+919990533327"
                className="flex items-center gap-3 text-slate-400 hover:text-[#C5A028] transition-colors text-sm"
              >
                <Phone className="w-4 h-4 text-[#C5A028] shrink-0" />
                +91-9990533327
              </a>
            </div>

            {/* Quick Inquiry */}
            <div className="bg-white/5 border border-white/10 rounded-xl p-4">
              <h5 className="text-xs font-bold font-syne text-white mb-2 uppercase tracking-wider">
                Quick Inquiry
              </h5>
              <p className="text-slate-400 text-xs mb-3">
                Have a question? Reach out to our study abroad experts.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 text-[#C5A028] hover:text-[#EAB308] text-xs font-semibold transition-colors"
              >
                Send us a message <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Sub-Footer - Shared Legal Bar */}
      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-5 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-slate-400 text-xs text-center md:text-left">
            &copy; {currentYear} Zafco Human Resource Management. All rights reserved.
          </p>
          <div className="flex items-center gap-4">
            <Link to="/study-abroad/privacy-policy/" className="text-slate-400 hover:text-[#C5A028] text-xs transition-colors" data-testid="sa-footer-privacy-link">
              Privacy Policy
            </Link>
            <Link to="/terms-and-conditions" className="text-slate-400 hover:text-slate-300 text-xs transition-colors">
              Terms of Service
            </Link>
            <Link to="/disclaimer" className="text-slate-400 hover:text-slate-300 text-xs transition-colors">
              Disclaimer
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default StudyAbroadFooter;
